import { type JSX } from 'react';

import { useCachedData } from 'Components/Context/CachedDataContext';
import { ColumnConfig, ColumnType } from 'Components/Table/Columns';
import { Table } from 'Components/Table/Table';
import { toTitleCase } from 'Helpers/StringUtils';
import { ExceptionHistoryModificationsResponse, ExceptionHistoryResponse } from 'Models/Exceptions';
import { OperationalControl } from 'Models/OperationalControls';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';

export interface ControlExceptionHistoryTableProps {
    type: ProductModule.CONTROLS;
    histories: ExceptionHistoryResponse[];
    displayMappedControlsModal: (mappedControls: OperationalControl[]) => void;
    onSelectHistory: (history: ExceptionHistoryResponse) => void;
}

export interface DueDiligenceExceptionHistoryTableProps {
    type: DueDiligenceModule;
    histories: ExceptionHistoryResponse[];
    getOrganizationName: (organizationId: string) => string;
    onSelectHistory: (history: ExceptionHistoryResponse) => void;
}

export type ExceptionHistoryTableProps = ControlExceptionHistoryTableProps | DueDiligenceExceptionHistoryTableProps;

export const ExceptionHistoryTable = (props: ExceptionHistoryTableProps): JSX.Element => {
    const cachedData = useCachedData();

    const columns: ColumnConfig<ExceptionHistoryResponse>[] = [
        {
            type: ColumnType.DATE,
            name: 'Date',
            getDateString: (history) => history.timestamp,
            getOnClickTextAction: () => ({ execute: props.onSelectHistory }),
        },
        {
            type: ColumnType.USER,
            name: 'Updated By',
            getUserId: (history) => history.exception.last_updated_by,
            users: cachedData.users,
        },
        {
            type: ColumnType.STRING,
            name: 'Status',
            getString: (history) => toTitleCase(history.exception.status),
        },
        {
            type: ColumnType.USER,
            name: 'Owner',
            getUserId: (history) => history.exception.owner_subject,
            users: cachedData.users,
        },
        {
            type: ColumnType.USER,
            name: 'Reviewer',
            getUserId: (history) => history.exception.reviewer_subject,
            users: cachedData.users,
        },
        props.type === ProductModule.CONTROLS
            ? {
                  type: ColumnType.MULTIPLE_CONTROLS,
                  name: 'Controls',
                  getControls: (history) => (history.exception as ExceptionHistoryModificationsResponse).impacted_controls,
                  onClick: props.displayMappedControlsModal,
              }
            : {
                  type: ColumnType.STRING,
                  name: props.type === ProductModule.UCRM ? 'Business' : 'Third Party',
                  getString: (history) => props.getOrganizationName(history.exception.impacted_vendor!),
              },
        {
            type: ColumnType.DATE,
            name: 'Expiration Date',
            getDateString: (history) => history.exception.expiration_date,
        },
    ];

    return <Table<ExceptionHistoryResponse> columns={columns} defaultSortColumn="Date" defaultSortDirection="descending" filters={[]} disableSorting getUniqueIdForRow={(history) => history.timestamp} rowsData={props.histories} />;
};
